import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import { Input } from '@/components/ui/input';
import { formatInt } from '@/lib/format';
import type { ConversionAccount, ConversionLinkStatus } from '@/types/api';

export function ConversionAccountsTable({ accounts }: { accounts: ConversionAccount[] }) {
  const { t, locale } = useI18n();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'clicks' | 'clicksPerThousandViews' | 'views'>('clicks');
  const rows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return accounts
      .filter((account) => account.username.toLocaleLowerCase().includes(query))
      .sort(
        (a, b) => (b[sortBy] ?? -1) - (a[sortBy] ?? -1) || a.username.localeCompare(b.username),
      );
  }, [accounts, search, sortBy]);
  const labels: Record<ConversionLinkStatus, string> = {
    ACTIVE: t('Lien actif', 'Active link'),
    INACTIVE: t('Lien inactif', 'Inactive link'),
    SHARED: t('Lien partagé', 'Shared link'),
    CHANGED: t('Lien modifié', 'Changed link'),
    UNMATCHED: t('Non rapproché', 'Unmatched'),
    NO_LINK: t('Sans lien', 'No link'),
    UNKNOWN: t('À vérifier', 'Unknown'),
  };
  const columns = [
    { key: 'clicks' as const, label: t('Clics destination', 'Destination clicks') },
    { key: 'views' as const, label: t('Vues gagnées', 'Views gained') },
    {
      key: 'clicksPerThousandViews' as const,
      label: t('Clics / 1 000 vues', 'Clicks / 1,000 views'),
    },
  ];
  return (
    <div className="min-w-0 overflow-hidden rounded-[14px] border border-line-1 bg-bg-1">
      <div className="flex flex-col gap-3 border-b border-line-1 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div>
          <h3 className="text-[14px] font-semibold text-text-1">
            {t(
              'Prioriser les comptes qui génèrent des clics',
              'Prioritize accounts that generate clicks',
            )}
          </h3>
          <p className="mt-1 text-[11px] leading-5 text-text-3">
            {t(
              'Comparez le volume de clics et le rendement. Cliquez sur une colonne pour classer les comptes.',
              'Compare click volume and efficiency. Select a column to rank accounts.',
            )}
          </p>
        </div>
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={t('Rechercher un compte GetMySocial', 'Search a GetMySocial account')}
          placeholder={t('Rechercher un compte', 'Search accounts')}
          className="sm:max-w-[220px]"
        />
      </div>
      <div
        className="max-h-[440px] overflow-auto"
        tabIndex={0}
        role="region"
        aria-label={t(
          'Tableau des conversions, défilement horizontal',
          'Conversion table, horizontal scrolling',
        )}
      >
        <table
          className="w-full min-w-[700px] border-collapse text-left text-[12px]"
          aria-label={t('Conversions par compte', 'Conversions by account')}
        >
          <thead className="sticky top-0 z-[1] bg-bg-2 text-text-3">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">
                {t('Compte', 'Account')}
              </th>
              <th scope="col" className="px-3 py-3 font-medium">
                {t('Lien observé', 'Observed link')}
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="px-3 text-right font-medium"
                  aria-sort={sortBy === column.key ? 'descending' : 'none'}
                >
                  <button
                    type="button"
                    className="min-h-11 whitespace-nowrap rounded py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
                    onClick={() => setSortBy(column.key)}
                  >
                    {column.label}
                    {sortBy === column.key ? ' ↓' : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((account) => (
              <tr key={account.accountId} className="border-t border-line-1 text-text-2">
                <td className="max-w-[240px] px-4 py-3">
                  <Link
                    to={`/accounts/${account.accountId}`}
                    className="block truncate font-medium text-text-1 hover:text-violet"
                    title={account.username}
                  >
                    @{account.username}
                  </Link>
                </td>
                <td className="max-w-[190px] px-3 py-3">
                  <span
                    className={account.linkStatus === 'ACTIVE' ? 'text-emerald' : 'text-text-3'}
                  >
                    {labels[account.linkStatus]}
                  </span>
                  {account.linkUrl && (
                    <p className="mt-0.5 truncate text-[10px] text-text-3" title={account.linkUrl}>
                      {account.linkUrl}
                    </p>
                  )}
                </td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums text-emerald">
                  {formatInt(account.clicks)}
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{formatInt(account.views)}</td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {account.clicksPerThousandViews === null
                    ? '—'
                    : account.clicksPerThousandViews.toLocaleString(locale, {
                        maximumFractionDigits: 2,
                      })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="p-6 text-center text-[12px] text-text-3">
            {t('Aucun compte à afficher.', 'No accounts to show.')}
          </p>
        )}
      </div>
      <p className="px-4 py-3 text-[11px] leading-5 text-text-3">
        {t(
          `${formatInt(rows.length)} comptes affichés sur ${formatInt(accounts.length)}. Un tiret indique une donnée absente ou une attribution incertaine.`,
          `${formatInt(rows.length)} of ${formatInt(accounts.length)} accounts shown. A dash indicates missing data or uncertain attribution.`,
        )}
      </p>
    </div>
  );
}
